import { useReducer, useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DiscardDialog } from "@/components/DiscardDialog";
import { CategoryNameInput } from "@/components/CategoryNameInput";
import { X, Check, Plus } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { createMenuCategory, getDefaultMenuCategories, getSettings } from "@/lib/api/admin";
import { isDuplicateCategoryName, DUPLICATE_CATEGORY_NAME_ERROR } from "@/lib/utils/menu";
import {
  validateRawSchedule,
  formToSchedule,
  scheduleToForm,
  type ScheduleFormState,
} from "@/lib/utils/schedule";
import { ScheduleInput } from "@/components/schedule/ScheduleInput";
import { Checkbox } from "@/components/ui/checkbox";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import type { MenuItem, MenuCategory } from "@/lib/api/types";
import type { RewardPrefill } from "./PointRewardFormDialog";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TextInput } from "@/components/TextInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/Textarea";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";
import { ImageUpload } from "@/components/ImageUpload";

// ─── Menu item form state ─────────────────────────────────────────────────────

type MenuItemFormState = {
  name: string;
  description: string;
  price: string; // dollar string e.g. "9.99"
  conditions: string[];
  conditionInput: string;
  categoryIds: string[];
  alwaysAvailable: boolean;
  availSchedule: ScheduleFormState;
  status: "active" | "inactive";
  imageFile: File | null;
  currentImageUrl?: string;
  addCatOpen: boolean;
  addCatName: string;
};

type FormAction =
  | {
      type: "set";
      field: keyof MenuItemFormState;
      value: string | string[] | File | null | boolean | undefined;
    }
  | { type: "addCondition" }
  | { type: "removeCondition"; index: number }
  | { type: "setAvailSchedule"; value: ScheduleFormState }
  | { type: "reset"; item?: MenuItem };

// Mirrors the backend's tiered points formula (see disca-docs/DOMAIN.md Computed Fields).
function pointsForPrice(priceCents: number): number {
  if (priceCents < 100) return 1;
  if (priceCents < 500) return Math.floor((priceCents * 3) / 100);
  return Math.floor((priceCents * 5) / 100);
}

function initForm(item?: MenuItem): MenuItemFormState {
  if (!item) {
    return {
      name: "",
      description: "",
      price: "",
      conditions: [],
      conditionInput: "",
      categoryIds: [],
      alwaysAvailable: true,
      availSchedule: scheduleToForm(),
      status: "active",
      imageFile: null,
      currentImageUrl: undefined,
      addCatOpen: false,
      addCatName: "",
    };
  }
  const priceStr = (item.price_cents / 100).toFixed(2);
  return {
    name: item.name,
    description: item.description ?? "",
    price: priceStr,
    conditions: item.conditions ?? [],
    conditionInput: "",
    categoryIds: item.category_ids ?? [],
    alwaysAvailable:
      !item.availability_schedule || Object.keys(item.availability_schedule).length === 0,
    availSchedule: scheduleToForm(item.availability_schedule ?? undefined),
    status: item.status,
    imageFile: null,
    currentImageUrl: item.image_url,
    addCatOpen: false,
    addCatName: "",
  };
}

function formReducer(state: MenuItemFormState, action: FormAction): MenuItemFormState {
  switch (action.type) {
    case "set":
      return { ...state, [action.field]: action.value };

    case "addCondition": {
      const trimmed = state.conditionInput.trim();
      if (!trimmed || trimmed.length < 3 || state.conditions.length >= 5) return state;
      return { ...state, conditions: [...state.conditions, trimmed], conditionInput: "" };
    }

    case "removeCondition":
      return { ...state, conditions: state.conditions.filter((_, i) => i !== action.index) };

    case "setAvailSchedule":
      return { ...state, availSchedule: action.value };

    case "reset":
      return initForm(action.item);

    default:
      return state;
  }
}

// ─── Menu Item Form Dialog ────────────────────────────────────────────────────

type MenuItemFormDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  storeCategory: string;
  priceLimitBypass: boolean;
  categories: MenuCategory[];
  item?: MenuItem;
  onSave: (data: Partial<MenuItem>, imageFile: File | null) => void;
  isPending: boolean;
  onMakePointReward?: (prefill: RewardPrefill) => void;
  onMakeStampReward?: (prefill: RewardPrefill) => void;
};

export function MenuItemFormDialog({
  open,
  onOpenChange,
  storeId,
  storeCategory,
  priceLimitBypass,
  categories,
  item,
  onSave,
  isPending,
  onMakePointReward,
  onMakeStampReward,
}: MenuItemFormDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [state, dispatch] = useReducer(formReducer, undefined, () => initForm(item));
  const [nameError, setNameError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [conditionError, setConditionError] = useState<string | null>(null);
  const [availErrors, setAvailErrors] = useState<Record<string, string>>({});
  const [availFormError, setAvailFormError] = useState<string | null>(null);
  const [addCatError, setAddCatError] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);

  const initialState = useMemo(() => initForm(item), [item]);

  const { data: defaultCategories = [] } = useQuery({
    queryKey: ["default-menu-categories"],
    queryFn: () => getDefaultMenuCategories(user!),
    staleTime: Infinity,
  });
  const defaultCategoryNames = defaultCategories.map((c) => c.name);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const addCatMut = useMutation({
    mutationFn: (name: string) => createMenuCategory(user!, storeId, name),
    onSuccess: (newCat) => {
      void qc.invalidateQueries({ queryKey: ["menu-categories", storeId] });
      dispatch({
        type: "set",
        field: "categoryIds",
        value: [...state.categoryIds, newCat.id],
      });
      dispatch({ type: "set", field: "addCatOpen", value: false });
      dispatch({ type: "set", field: "addCatName", value: "" });
    },
    onError: () => setAddCatError("Failed to create category"),
  });

  const maxPriceDollars = priceLimitBypass
    ? BYPASS_MAX_PRICE_DOLLARS
    : storeCategory === "nightclub"
      ? (settings?.nightclub_max_price_cents ?? 50000) / 100
      : (settings?.default_max_price_cents ?? 30000) / 100;

  function tryClose() {
    const dirty =
      state.imageFile !== null ||
      state.currentImageUrl !== initialState.currentImageUrl ||
      state.name !== initialState.name ||
      state.description !== initialState.description ||
      state.price !== initialState.price ||
      state.status !== initialState.status ||
      state.alwaysAvailable !== initialState.alwaysAvailable ||
      JSON.stringify(state.conditions) !== JSON.stringify(initialState.conditions) ||
      JSON.stringify(state.categoryIds) !== JSON.stringify(initialState.categoryIds) ||
      JSON.stringify(state.availSchedule) !== JSON.stringify(initialState.availSchedule) ||
      state.conditionInput.trim() !== "" ||
      (state.addCatOpen && state.addCatName.trim() !== "");
    if (dirty) {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  async function handleSave() {
    setNameError(null);
    setPriceError(null);
    setConditionError(null);
    setAddCatError("");
    setAvailErrors({});
    setAvailFormError(null);

    if (!state.name.trim()) {
      setNameError("Name is required.");
      return;
    }
    if (state.name.trim().length < 2) {
      setNameError("Name must be at least 2 characters.");
      return;
    }

    const priceCents = Math.round(parseFloat(state.price) * 100);
    if (isNaN(priceCents) || priceCents < 1) {
      setPriceError("Price must be at least $0.01.");
      return;
    }
    if (priceCents > maxPriceDollars * 100) {
      setPriceError(`Price cannot exceed $${maxPriceDollars.toLocaleString()}.`);
      return;
    }

    let availSchedulePayload: Record<string, { open: string; close: string }[]> | null = null;
    if (!state.alwaysAvailable) {
      const rawSchedule = formToSchedule(state.availSchedule);
      if (Object.keys(rawSchedule).length === 0) {
        setAvailFormError("Select at least one day.");
        return;
      }
      const schedErrs = validateRawSchedule(rawSchedule);
      if (schedErrs) {
        setAvailErrors(schedErrs);
        return;
      }
      availSchedulePayload = rawSchedule;
    }

    // Auto-save any pending condition text using the same rule as the Add button.
    let conditions = state.conditions;
    const trimmedCondition = state.conditionInput.trim();
    if (trimmedCondition) {
      if (trimmedCondition.length < 3) {
        setConditionError("Must be at least 3 characters.");
        return;
      }
      if (conditions.length < 5) {
        conditions = [...conditions, trimmedCondition];
        dispatch({ type: "addCondition" });
      }
    }

    // Auto-save any pending category text using the same rule as the Add button.
    let categoryIds = state.categoryIds;
    if (state.addCatOpen) {
      const catName = state.addCatName.trim();
      if (catName) {
        if (isDuplicateCategoryName(catName, categories)) {
          setAddCatError(DUPLICATE_CATEGORY_NAME_ERROR);
          return;
        }
        try {
          const newCat = await addCatMut.mutateAsync(catName);
          categoryIds = [...categoryIds, newCat.id];
        } catch {
          setAddCatError("Failed to create category");
          return;
        }
      }
    }

    const data: Partial<MenuItem> = {
      name: state.name.trim(),
      description: state.description.trim() || undefined,
      price_cents: priceCents,
      conditions,
      category_ids: categoryIds,
      availability_schedule: availSchedulePayload,
      status: state.status,
      image_url: state.currentImageUrl,
    };

    onSave(data, state.imageFile);
  }

  const livePrice = parseFloat(state.price);
  const livePriceCents = !isNaN(livePrice) && livePrice >= 0 ? Math.round(livePrice * 100) : 0;
  const effectivePoints = pointsForPrice(livePriceCents);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{item ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <TextInput
              id="mi-name"
              label="Name *"
              value={state.name}
              onChange={(e) => dispatch({ type: "set", field: "name", value: e.target.value })}
              placeholder="Item name"
              maxLength={50}
              error={nameError ?? undefined}
            />

            {/* Description */}
            <Textarea
              id="mi-description"
              label="Description"
              value={state.description}
              onChange={(e) =>
                dispatch({ type: "set", field: "description", value: e.target.value })
              }
              placeholder="Optional description"
              rows={3}
              maxLength={700}
            />

            {/* Image */}
            <ImageUpload
              label="Image"
              aspectRatio="1:1"
              currentUrl={state.currentImageUrl}
              onFileSelect={(file) => dispatch({ type: "set", field: "imageFile", value: file })}
              onClear={() => {
                dispatch({ type: "set", field: "imageFile", value: null });
                dispatch({ type: "set", field: "currentImageUrl", value: undefined });
              }}
              className="w-44"
            />

            {/* Price */}
            <div className="space-y-1.5">
              <Label htmlFor="mi-price">Price *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  $
                </span>
                <Input
                  id="mi-price"
                  className="pl-6"
                  type="text"
                  inputMode="decimal"
                  value={state.price}
                  aria-invalid={!!priceError}
                  onChange={(e) =>
                    dispatch({
                      type: "set",
                      field: "price",
                      value: e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"),
                    })
                  }
                  placeholder="0.00"
                />
              </div>
              {priceError ? (
                <p className="text-xs text-destructive">{priceError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Max ${maxPriceDollars.toLocaleString()}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {effectivePoints} {effectivePoints === 1 ? "point" : "points"} earned
              </p>
            </div>

            {/* Categories */}
            <div className="space-y-1.5">
              <Label>Categories</Label>
              <div className="space-y-1">
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((cat) => {
                    const selected = state.categoryIds.includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() =>
                          dispatch({
                            type: "set",
                            field: "categoryIds",
                            value: selected
                              ? state.categoryIds.filter((id) => id !== cat.id)
                              : [...state.categoryIds, cat.id],
                          })
                        }
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                        }`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}

                  {state.addCatOpen ? (
                    <div className="flex items-center gap-1">
                      <CategoryNameInput
                        autoFocus
                        value={state.addCatName}
                        onChange={(v) => {
                          dispatch({ type: "set", field: "addCatName", value: v });
                          if (addCatError) setAddCatError("");
                        }}
                        onSelectSuggestion={() => setAddCatError("")}
                        suggestions={defaultCategoryNames}
                        existingNames={categories.map((c) => c.name)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const name = state.addCatName.trim();
                            if (!name) return;
                            if (isDuplicateCategoryName(name, categories)) {
                              setAddCatError(DUPLICATE_CATEGORY_NAME_ERROR);
                              return;
                            }
                            addCatMut.mutate(name);
                          }
                          if (e.key === "Escape") {
                            dispatch({ type: "set", field: "addCatOpen", value: false });
                            dispatch({ type: "set", field: "addCatName", value: "" });
                            setAddCatError("");
                          }
                        }}
                        placeholder="Category name"
                        maxLength={100}
                        disabled={addCatMut.isPending}
                        inputClassName="h-7 rounded-full border border-input bg-transparent px-3 text-xs w-36 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const name = state.addCatName.trim();
                          if (!name) return;
                          if (isDuplicateCategoryName(name, categories)) {
                            setAddCatError(DUPLICATE_CATEGORY_NAME_ERROR);
                            return;
                          }
                          addCatMut.mutate(name);
                        }}
                        disabled={!state.addCatName.trim() || addCatMut.isPending}
                        className="text-primary hover:text-primary/80 disabled:opacity-40"
                      >
                        <Check className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          dispatch({ type: "set", field: "addCatOpen", value: false });
                          dispatch({ type: "set", field: "addCatName", value: "" });
                          setAddCatError("");
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => dispatch({ type: "set", field: "addCatOpen", value: true })}
                      disabled={isPending}
                      className="rounded-full px-3 py-1 text-xs font-medium border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                    >
                      <Plus className="size-3 inline mr-1" />
                      Add category
                    </button>
                  )}
                </div>
                {addCatError && <p className="text-xs text-destructive">{addCatError}</p>}
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-1.5">
              <Label htmlFor="mi-cond-input">Conditions (max 5)</Label>
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-1.5">
                  <Input
                    id="mi-cond-input"
                    value={state.conditionInput}
                    onChange={(e) => {
                      dispatch({ type: "set", field: "conditionInput", value: e.target.value });
                      setConditionError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmed = state.conditionInput.trim();
                        if (trimmed && trimmed.length < 3) {
                          setConditionError("Must be at least 3 characters.");
                        } else {
                          setConditionError(null);
                          dispatch({ type: "addCondition" });
                        }
                      }
                    }}
                    placeholder="e.g. Dine-in only"
                    maxLength={60}
                    disabled={state.conditions.length >= 5}
                  />
                  <p
                    className={`text-xs text-right ${
                      state.conditionInput.length > 60
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {state.conditionInput.length}/60
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const trimmed = state.conditionInput.trim();
                    if (trimmed && trimmed.length < 3) {
                      setConditionError("Must be at least 3 characters.");
                    } else {
                      setConditionError(null);
                      dispatch({ type: "addCondition" });
                    }
                  }}
                  disabled={!state.conditionInput.trim() || state.conditions.length >= 5}
                >
                  Add
                </Button>
              </div>
              {conditionError && <p className="text-xs text-destructive">{conditionError}</p>}
              {state.conditions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {state.conditions.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs"
                    >
                      {c}
                      <button
                        type="button"
                        onClick={() => dispatch({ type: "removeCondition", index: i })}
                        className="hover:text-foreground"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Availability */}
            <div className="space-y-2">
              <Label>Availability</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={state.alwaysAvailable}
                  onCheckedChange={(checked) =>
                    dispatch({ type: "set", field: "alwaysAvailable", value: !!checked })
                  }
                  aria-label="Available 24/7"
                />
                <span className="text-sm">Available everyday</span>
              </label>
              {!state.alwaysAvailable && (
                <>
                  <ScheduleInput
                    value={state.availSchedule}
                    onChange={(v) => {
                      dispatch({ type: "setAvailSchedule", value: v });
                      setAvailErrors({});
                      setAvailFormError(null);
                    }}
                    errors={availErrors}
                    defaultSlot={{ open: "10:00", close: "22:00" }}
                  />
                  {availFormError && (
                    <p className="text-xs text-destructive mt-2">{availFormError}</p>
                  )}
                </>
              )}
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status</Label>
              <SegmentedToggle
                value={state.status}
                onChange={(s) => dispatch({ type: "set", field: "status", value: s })}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
              />
            </div>
          </div>

          {item && (onMakePointReward || onMakeStampReward) && (
            <div className="space-y-2 pt-1 border-t">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Make Reward
              </p>
              <div className="flex flex-wrap gap-2">
                {onMakePointReward && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isPending}
                    onClick={() =>
                      onMakePointReward({
                        name: state.name.trim(),
                        description: state.description.trim() || undefined,
                        priceCents: Math.round(parseFloat(state.price) * 100) || 0,
                        imageUrl: state.imageFile ? undefined : state.currentImageUrl,
                        imageFile: state.imageFile ?? undefined,
                      })
                    }
                  >
                    Make Point Reward
                  </Button>
                )}
                {onMakeStampReward && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isPending}
                    onClick={() =>
                      onMakeStampReward({
                        name: state.name.trim(),
                        description: state.description.trim() || undefined,
                        priceCents: Math.round(parseFloat(state.price) * 100) || 0,
                        imageUrl: state.imageFile ? undefined : state.currentImageUrl,
                        imageFile: state.imageFile ?? undefined,
                      })
                    }
                  >
                    Make Stamp Reward
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={tryClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending || addCatMut.isPending}>
              {isPending || addCatMut.isPending ? (
                <Spinner className="size-4 border-primary-foreground" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
